import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FLAT_RATES, INFAKT_KIND_LABEL, fmtD, mergeSettings, type CompanyField, type PackNeeds, type PrintData } from '@biuro/shared';
import { api } from '../api.ts';
import { useData, useMe, useSaveDoc } from '../hooks.tsx';
import { Sheet } from '../ui.tsx';

const COMPANY_LABEL: Record<CompanyField, string> = { companyName: 'Nazwa firmy', nip: 'NIP', address: 'Adres' };
const key = (uuid: string, index: number, field: 'name' | 'flat') => `${uuid}:${index}:${field}`;

/**
 * Okno przed pobraniem paczki: inFakt nie oddał PDF-u dokumentów wewnętrznych, a do wydruku z Biura brakuje
 * nazw pozycji, stawek ryczałtu albo danych firmy. Pyta tylko o brakujące pola; kwoty są wyłącznie z inFaktu.
 */
export function PrintDataForm({ needs, onClose, onSaved, onSkip }: {
  needs: PackNeeds; onClose: () => void;
  /** Ponowne pobranie paczki po zapisie – okno czeka na nie i pokazuje postęp. */
  onSaved: () => Promise<void>; onSkip: () => Promise<void>;
}) {
  const { data } = useData();
  const me = useMe();
  const save = useSaveDoc();
  const rates = useQuery({ queryKey: ['flatRates'], queryFn: api.infaktFlatRates, enabled: !!me.data?.infakt.enabled, staleTime: 3600_000, retry: false });
  const [vals, setVals] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const d of needs.docs) for (const l of d.lines) { v[key(d.uuid, l.index, 'name')] = l.name; v[key(d.uuid, l.index, 'flat')] = l.flat; }
    return v;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<'' | 'save' | 'pack'>('');
  const set = (k: string, v: string) => setVals((x) => ({ ...x, [k]: v }));
  const flatOptions = (cur: string) => [...new Set([...(rates.data?.map((r) => r.symbol) || FLAT_RATES), ...(cur ? [cur] : [])])];

  const submit = async () => {
    const errs: Record<string, string> = {};
    for (const d of needs.docs) {
      for (const l of d.lines) {
        if (l.missing.includes('name') && !vals[key(d.uuid, l.index, 'name')]?.trim()) errs[key(d.uuid, l.index, 'name')] = 'Wpisz nazwę pozycji.';
        if (l.missing.includes('flat') && !vals[key(d.uuid, l.index, 'flat')]) errs[key(d.uuid, l.index, 'flat')] = 'Wybierz stawkę ryczałtu.';
      }
    }
    for (const c of needs.company) if (!vals[`company:${c}`]?.trim()) errs[`company:${c}`] = 'Uzupełnij to pole.';
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy('save');
    try {
      for (const d of needs.docs) {
        // Zapisujemy tylko pola, o które okno pytało; reszta zostaje pusta (= dane z inFaktu) albo z wcześniejszego zapisu.
        const prev = data.printData?.[d.uuid];
        const same = prev && prev.kind === d.kind && prev.number === d.number ? prev : undefined;
        const n = Math.max(same?.lines.length || 0, ...d.lines.map((l) => l.index + 1));
        const lines = Array.from({ length: n }, (_, i) => ({ name: same?.lines[i]?.name || '', flat: same?.lines[i]?.flat || '' }));
        for (const l of d.lines) {
          lines[l.index] = {
            name: l.missing.includes('name') ? vals[key(d.uuid, l.index, 'name')].trim() : lines[l.index].name,
            flat: l.missing.includes('flat') ? vals[key(d.uuid, l.index, 'flat')] : lines[l.index].flat,
          };
        }
        const doc: PrintData = { kind: d.kind, number: d.number, lines, updatedAt: new Date().toISOString() };
        await save.mutateAsync({ col: 'printData', id: d.uuid, doc });
      }
      if (needs.company.length) {
        const company = Object.fromEntries(needs.company.map((c) => [c, vals[`company:${c}`].trim()]));
        await save.mutateAsync({ col: 'settings', id: 'main', doc: { ...mergeSettings(data.settings.main), ...company } });
      }
      setBusy('pack');
      await onSaved();
    } catch {
      // Błąd zapisu pokazuje useSaveDoc; okno zostaje otwarte.
    } finally {
      setBusy('');
    }
  };
  const skip = async () => {
    setBusy('pack');
    try {
      await onSkip();
    } finally {
      setBusy('');
    }
  };

  const err = (k: string) => errors[k] && <div className="hint bad">{errors[k]}</div>;

  return (
    <Sheet title="Uzupełnij dane do wydruku" onClose={onClose} dismissOnBackdrop={false}
      footer={<>
        <button className="btn" onClick={onClose}>Anuluj</button>
        <div className="r">
          <button className="btn" disabled={!!busy} onClick={() => void skip()}>Pomiń wydruki</button>
          <button className="btn pri" disabled={!!busy} onClick={() => void submit()}>
            {busy === 'save' ? 'Zapisuję…' : busy === 'pack' ? 'Przygotowuję paczkę…' : 'Zapisz i pobierz paczkę'}
          </button>
        </div>
      </>}>
      <div className="stack">
        <p className="muted">inFakt nie oddał PDF-u tych dokumentów. Aplikacja zrobi wydruk z ich danych – brakuje tylko tego:</p>
        {needs.company.length > 0 && (
          <section className="fgrid">
            <h3 className="full">Dane firmy</h3>
            {needs.company.map((c) => (
              <div key={c} className={`fld${c === 'address' ? ' full' : ''}`}>
                <label htmlFor={`pd-company-${c}`}>{COMPANY_LABEL[c]}</label>
                <input id={`pd-company-${c}`} autoComplete="off" value={vals[`company:${c}`] || ''} onChange={(e) => set(`company:${c}`, e.target.value)} />
                {err(`company:${c}`)}
              </div>
            ))}
          </section>
        )}
        {needs.docs.map((d) => (
          <section key={d.uuid} className="stack">
            <h3>{INFAKT_KIND_LABEL[d.kind]} {d.number} z {fmtD(d.date)}</h3>
            {d.lines.map((l) => {
              const kn = key(d.uuid, l.index, 'name'), kf = key(d.uuid, l.index, 'flat');
              return (
                <div key={l.index} className="fgrid pd-line">
                  <div className="full muted small">Pozycja {l.index + 1}: {l.amounts}{!l.missing.includes('name') && l.name ? ` · ${l.name}` : ''}</div>
                  {l.missing.includes('name') && (
                    <div className="fld">
                      <label htmlFor={`pd-${kn}`}>Nazwa pozycji</label>
                      <input id={`pd-${kn}`} autoComplete="off" value={vals[kn] || ''} onChange={(e) => set(kn, e.target.value)} />
                      {err(kn)}
                    </div>
                  )}
                  {l.missing.includes('flat') && (
                    <div className="fld">
                      <label htmlFor={`pd-${kf}`}>Stawka ryczałtu</label>
                      <select id={`pd-${kf}`} value={vals[kf] || ''} onChange={(e) => set(kf, e.target.value)}>
                        <option value="">— wybierz —</option>
                        {flatOptions(l.flat).map((r) => <option key={r} value={r}>{r.replace('.', ',')}%</option>)}
                      </select>
                      {err(kf)}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        ))}
      </div>
    </Sheet>
  );
}
